import {useEffect, useState} from 'react';

export type DateType = Date | null;

export type SelectDatesType = {
	firstDate: DateType;
	secondDate: DateType;
};

interface UseModalDatePickerProps {
	currentFirstDate?: Date;
	currentSecondDate?: Date;
	selectFirstDate: (date: DateType) => void;
	selectSecondDate?: (date: DateType) => void;
	selectDates?: (dates: SelectDatesType) => void;
	onAbandonModal?: () => void;
	isSecondDateOptional?: boolean;
	adjustDateInterval?: boolean;
}

export const useModalDatePicker = ({
	currentFirstDate,
	currentSecondDate,
	selectFirstDate,
	selectSecondDate,
	selectDates,
	onAbandonModal,
	isSecondDateOptional = false,
	adjustDateInterval = false,
}: UseModalDatePickerProps) => {
	const [firstDate, setFirstDate] = useState<Date | undefined>(currentFirstDate);
	const [secondDate, setSecondDate] = useState<Date | undefined>(currentSecondDate);
	const [openDatePickerState, setOpenDatePickerState] = useState(false);
	const [alternateSecondaryDateState, setAlternateDateState] = useState<Date>(new Date());
	const [disableConfirmButtonState, setDisableConfirmButtonState] = useState(false);

	const handleSetAlternateDate = (date: Date | undefined) => {
		if (adjustDateInterval && date) {
			const threeMonthsFromFirstDate = new Date(date);
			threeMonthsFromFirstDate.setMonth(threeMonthsFromFirstDate.getMonth() + 3);
			threeMonthsFromFirstDate.setDate(threeMonthsFromFirstDate.getDate() + 1);

			const today = new Date();

			const endDate = threeMonthsFromFirstDate > today ? today : threeMonthsFromFirstDate;

			setAlternateDateState(endDate);
		} else {
			setAlternateDateState(new Date());
		}
	};

	const handleDisableConfirmButton = (startDate?: Date, endDate?: Date | null) => {
		const shouldDisableButton =
			isSecondDateOptional &&
			startDate?.getTime() === currentFirstDate?.getTime() &&
			endDate?.getTime() === currentSecondDate?.getTime();

		if (shouldDisableButton) {
			setDisableConfirmButtonState(true);
			return;
		}

		setDisableConfirmButtonState(false);
	};

	const handleChangeDate = (valueFirstDate: Date, valueSecondDate: Date | null) => {
		setFirstDate(valueFirstDate);
		handleSetAlternateDate(valueFirstDate);

		if (valueSecondDate) {
			setSecondDate(valueSecondDate);
		} else if (adjustDateInterval) {
			setSecondDate(undefined);
		}

		handleDisableConfirmButton(valueFirstDate, valueSecondDate);
	};

	const handleOpenDatePicker = () => {
		setOpenDatePickerState(true);
	};

	const handleCloseDatePicker = () => {
		setOpenDatePickerState(false);
		if (onAbandonModal) {
			onAbandonModal();
		}
	};

	const handleConfirmDate = () => {
		const setSecondDateOptional =
			firstDate && !secondDate && selectSecondDate && isSecondDateOptional;

		if (firstDate) {
			selectFirstDate(firstDate);
		}

		if (secondDate && selectSecondDate) {
			selectSecondDate(secondDate);
		}

		if (setSecondDateOptional) {
			selectSecondDate(firstDate);
			if (selectDates) {
				selectDates({firstDate, secondDate: firstDate});
			}
		}

		if (firstDate && secondDate && selectDates) {
			selectDates({firstDate, secondDate});
		}

		handleCloseDatePicker();
	};

	const handleClearDate = () => {
		if (firstDate) {
			setFirstDate(undefined);
			selectFirstDate(null);
		}

		if (secondDate) {
			setSecondDate(undefined);
		}

		if (selectSecondDate) {
			selectSecondDate(null);
		}

		if (selectDates) {
			selectDates({firstDate: null, secondDate: null});
		}

		handleCloseDatePicker();
	};

	useEffect(() => {
		setFirstDate(currentFirstDate);
		setSecondDate(currentSecondDate);
		handleDisableConfirmButton(firstDate, secondDate);
		handleSetAlternateDate(currentFirstDate);
	}, [currentFirstDate, currentSecondDate]);

	return {
		firstDate,
		setFirstDate,
		secondDate,
		setSecondDate,
		openDatePickerState,
		handleChangeDate,
		handleOpenDatePicker,
		handleCloseDatePicker,
		handleConfirmDate,
		handleClearDate,
		alternateSecondaryDateState,
		disableConfirmButtonState,
	};
};
